import type { ServiceGroup as ServiceGroupModel } from "@prisma/client";
import Heading from "~/ui/Heading";
import List from "~/ui/List";
import SubtleAccordionGroup from "~/ui/SubtleAccordionGroup";
import shouldOpenPerDefault, {
  ROW_COUNT_LIMIT,
} from "~/service/helpers/shouldOpenPerDefault";
import ServiceListItem from "./ServiceListItem";
import ServiceGroup from "./ServiceGroup";
import Button from "~/ui/Button";
import { useState } from "react";
import useTranslation from "~/kernel/internationalization/useTranslation";
import type { CompanyPageService } from "./findCompanyPageServices";
import { buildServicesViewObject } from "./buildServicesViewObject";

type ServicesProps = {
  services: CompanyPageService[];
  groups: ServiceGroupModel[];
};

export default function Services({ services, groups }: ServicesProps) {
  const [showAll, setShowAll] = useState(false);
  const { tt } = useTranslation("companyServices");
  const viewObject = buildServicesViewObject({
    services,
    serviceGroups: groups,
  });

  switch (viewObject.display) {
    case "NO_SERVICES":
      return (
        <div className="space-y-5">
          <Heading level={2}>{tt("heading")}</Heading>
          <p>{tt("noServices")}</p>
        </div>
      );
    case "SERVICE_GROUPS": {
      const defaultOpen = shouldOpenPerDefault(viewObject.serviceGroups);
      return (
        <div className="space-y-5">
          <Heading level={2}>{tt("heading")}</Heading>
          <SubtleAccordionGroup>
            {viewObject.serviceGroups.map((group) => (
              <ServiceGroup
                defaultOpen={defaultOpen}
                key={group.id}
                group={group}
              />
            ))}
          </SubtleAccordionGroup>
        </div>
      );
    }
    case "SERVICES": {
      const mainServices = viewObject.services;
      const expandedServices = mainServices.slice(0, ROW_COUNT_LIMIT);
      const collapsedServices = mainServices.slice(ROW_COUNT_LIMIT);
      return (
        <div className="space-y-5">
          <Heading level={2}>{tt("heading")}</Heading>
          <div>
            <List>
              {expandedServices.map((service) => (
                <ServiceListItem trim key={service.id} service={service} />
              ))}
              {showAll &&
                collapsedServices.map((service) => (
                  <ServiceListItem trim key={service.id} service={service} />
                ))}
            </List>
          </div>
          <ShowAllButton
            hasHidden={collapsedServices.length > 0}
            onClick={() => setShowAll(!showAll)}
            showAll={showAll}
          />
        </div>
      );
    }
  }
}

type ShowAllButtonProps = {
  hasHidden: boolean;
  onClick: () => void;
  showAll: boolean;
};

function ShowAllButton({ hasHidden, onClick, showAll }: ShowAllButtonProps) {
  const { tt } = useTranslation("companyServices");

  if (!hasHidden) {
    return null;
  }

  return (
    <Button variant="text" onClick={onClick}>
      {showAll ? tt("showFewer") : tt("showAll")}
    </Button>
  );
}
